import { useReactiveVar } from '@apollo/client';
import { Logout, ManageAccountsOutlined, PermIdentity, PersonOutline } from '@mui/icons-material';
import { Avatar, Box, CSSObject, Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import useAuthWithCognito from 'hooks/useAuthWithCognito/useAuth';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { Link as RouterLink } from 'react-router';
import { authVar } from 'state/vars';
import { isSecurityEnabled } from 'v2/utils/isSecurityEnabled';
import packageJson from '../../../../package.json';

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClose: () => void;
}

const truncateText: CSSObject = {
  display: 'block',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
};

const ProfileMenu = ({ anchorEl, open, onOpen, onClose }: Props) => {
  const { translation } = useTranslation();
  const { handleLogout: logout } = useAuthWithCognito();
  const { user } = useReactiveVar(authVar);

  const securityEnabled = isSecurityEnabled(user);

  const handleLogout = () => {
    onClose();
    logout();
  };

  return (
    <>
      <IconButton
        color="inherit"
        id="appbar-profile"
        aria-haspopup="true"
        aria-controls={open ? 'appbar-profile-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={onOpen}
      >
        <PermIdentity />
      </IconButton>
      <Menu
        id="appbar-profile-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Box display="flex" gap="12px" sx={{ padding: '6px 16px 12px', alignItems: 'center' }}>
          <Avatar sx={{ height: 36, width: 36 }}>{user?.name?.[0] || '?'}</Avatar>
          <Box sx={{ flex: '1', minWidth: '0', maxWidth: '240px' }}>
            <Typography variant="body2" sx={{ margin: 0, ...truncateText }}>
              {user?.name || 'Profile'}
            </Typography>
            {user?.email && (
              <Typography variant="caption" sx={truncateText}>
                {user.email}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 1 }} />
        {user?.isAdmin && securityEnabled && (
          <MenuItem id="account-settings-menu-item" component={RouterLink} to="/account-settings" onClick={onClose}>
            <ListItemIcon>
              <ManageAccountsOutlined />
            </ListItemIcon>
            {translation.account}
          </MenuItem>
        )}
        {user && (
          <MenuItem
            id="my-profile-menu-item"
            component={RouterLink}
            to={user.isAdmin ? `/users/${user.id}` : '/my-profile'}
            onClick={onClose}
          >
            <ListItemIcon>
              <PersonOutline />
            </ListItemIcon>
            {translation.my_profile}
          </MenuItem>
        )}
        <MenuItem id="logout-menu-item" onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          {translation.logout}
        </MenuItem>
        <Divider />
        <Typography
          variant="caption"
          sx={{ display: 'block', padding: '6px 16px', color: (theme) => theme.palette.text.disabled }}
        >
          Version: {packageJson.version}
        </Typography>
      </Menu>
    </>
  );
};

export default ProfileMenu;
